import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { JOB_STATUSES, JobCardBody } from "@/components/app/JobCard";

const DOT = { draft: "bg-violet-500", open: "bg-indigo-500", on_hold: "bg-amber-500", closed: "bg-slate-400" };

export default function JobsBoard({ jobs, onMoved }) {
  const [dragId, setDragId] = useState(null);
  const [over, setOver] = useState(null);
  const [local, setLocal] = useState({});

  const statusOf = (j) => local[j.id] || j.status || "open";

  const drop = async (status) => {
    const j = jobs.find(x => x.id === dragId);
    setOver(null); setDragId(null);
    if (!j || statusOf(j) === status) return;
    if (j.can_edit === false) { toast.error("Only the job owner or a Tenant Admin can change this job"); return; }
    setLocal(p => ({ ...p, [j.id]: status }));
    try {
      await api.put(`/jobs/${j.id}`, { status });
      toast.success(`"${j.title}" moved to ${JOB_STATUSES.find(s => s.key === status).label}`);
      await onMoved();
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || "Could not update status");
    } finally {
      setLocal(p => { const n = { ...p }; delete n[j.id]; return n; });
    }
  };

  return (
    <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5 items-start" data-testid="jobs-board">
      {JOB_STATUSES.map(col => {
        const items = jobs.filter(j => statusOf(j) === col.key);
        return (
          <section key={col.key} data-testid={`board-col-${col.key}`}
            onDragOver={(e) => { e.preventDefault(); setOver(col.key); }}
            onDragLeave={() => setOver(o => (o === col.key ? null : o))}
            onDrop={(e) => { e.preventDefault(); drop(col.key); }}
            className={`rounded-xl border p-3 min-h-[240px] transition-colors ${over === col.key ? "border-indigo-400 bg-indigo-50/60" : "border-[color:var(--bd-sub)] bg-slate-50/70"}`}>
            <header className="flex items-center justify-between px-1 pb-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                <span className={`w-2 h-2 rounded-full ${DOT[col.key]}`} />{col.label}
              </div>
              <span className="text-xs text-slate-500 bg-white border border-[color:var(--bd-sub)] rounded-full px-2 py-0.5" data-testid={`board-count-${col.key}`}>{items.length}</span>
            </header>
            <div className="flex flex-col gap-3">
              {items.map(j => (
                <div key={j.id} draggable data-testid={`board-card-${j.id}`}
                  onDragStart={(e) => { setDragId(j.id); e.dataTransfer.effectAllowed = "move"; }}
                  onDragEnd={() => { setDragId(null); setOver(null); }}
                  className={`tp-card p-4 cursor-grab active:cursor-grabbing hover:shadow-md transition-[box-shadow,opacity] ${dragId === j.id ? "opacity-50" : ""}`}>
                  <Link to={`/app/jobs/${j.id}`} draggable={false} className="flex flex-col">
                    <JobCardBody j={{ ...j, status: statusOf(j) }} compact />
                  </Link>
                </div>
              ))}
              {items.length === 0 && <div className="text-xs text-slate-400 text-center py-8 border border-dashed border-[color:var(--bd-sub)] rounded-lg">Drag jobs here</div>}
            </div>
          </section>
        );
      })}
    </div>
  );
}
